import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Button,
  Stack,
  Divider,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import DownloadIcon from '@mui/icons-material/Download';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FitScreenIcon from '@mui/icons-material/FitScreen';

// Configure PDF.js worker - serve from public folder (no CORS issues)
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

interface PDFViewerDrawerProps {
  open: boolean;
  onClose: () => void;
  pdfUrl: string;
  fileName?: string;
}

const PDFViewerDrawer: React.FC<PDFViewerDrawerProps> = ({
  open,
  onClose,
  pdfUrl,
  fileName = 'Document'
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Reset state when drawer opens with new PDF
  useEffect(() => {
    if (open) {
      setPageNumber(1);
      setScale(1.0);
      setLoading(true);
      setError(null);
      setNumPages(null);
    }
  }, [open, pdfUrl]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('Error loading PDF:', error);
    setError('Failed to load PDF. Please try again.');
    setLoading(false);
  };

  const handleClose = () => {
    // Reset state when closing
    setPageNumber(1);
    setScale(1.0);
    setLoading(true);
    setError(null);
    setNumPages(null);
    onClose();
  };

  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(prev + 1, numPages || 1));
  };

  const zoomIn = () => {
    setScale((prev) => Math.min(prev + 0.2, 3.0));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - 0.2, 0.5));
  };

  const fitToWidth = () => {
    setScale(1.0);
  };

  const handleDownload = () => {
    window.open(pdfUrl, '_blank');
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '80%', md: '60%', lg: '50%' },
          maxWidth: '800px'
        }
      }}
      sx={{
        zIndex: 11000  // Higher than claims drawer (9999) and correspondence modal (10000)
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider'
          }}
        >
          <Typography variant="h6" noWrap sx={{ flexGrow: 1, mr: 2 }}>
            {fileName}
          </Typography>
          <IconButton onClick={handleClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Toolbar */}
        <Box
          sx={{
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
            flexWrap: 'wrap',
            gap: 1
          }}
        >
          {/* Page Navigation */}
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              onClick={goToPrevPage}
              disabled={pageNumber <= 1 || loading}
              size="small"
            >
              <NavigateBeforeIcon />
            </IconButton>
            <Typography variant="body2" sx={{ minWidth: '100px', textAlign: 'center' }}>
              {numPages ? `Page ${pageNumber} of ${numPages}` : 'Loading...'}
            </Typography>
            <IconButton
              onClick={goToNextPage}
              disabled={pageNumber >= (numPages || 1) || loading}
              size="small"
            >
              <NavigateNextIcon />
            </IconButton>
          </Stack>

          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />

          {/* Zoom Controls */}
          <Stack direction="row" spacing={1}>
            <IconButton onClick={zoomOut} disabled={loading} size="small" title="Zoom Out">
              <ZoomOutIcon />
            </IconButton>
            <Typography variant="body2" sx={{ minWidth: '50px', textAlign: 'center', lineHeight: '40px' }}>
              {Math.round(scale * 100)}%
            </Typography>
            <IconButton onClick={zoomIn} disabled={loading} size="small" title="Zoom In">
              <ZoomInIcon />
            </IconButton>
            <IconButton onClick={fitToWidth} disabled={loading} size="small" title="Fit to Width">
              <FitScreenIcon />
            </IconButton>
          </Stack>

          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />

          {/* Download Button */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
          >
            Download
          </Button>
        </Box>

        {/* PDF Content */}
        <Box
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            p: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: loading ? 'center' : 'flex-start',
            backgroundColor: '#f5f5f5'
          }}
        >
          {loading && !error && (
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Loading PDF...
              </Typography>
            </Box>
          )}

          {error && (
            <Box sx={{ textAlign: 'center', maxWidth: '500px' }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                The PDF viewer couldn't load this document. You can still view it directly in a new tab.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => window.open(pdfUrl, '_blank')}
                sx={{ mr: 1 }}
              >
                Open in New Tab
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  // Try to download directly
                  const link = document.createElement('a');
                  link.href = pdfUrl;
                  link.download = fileName || 'document.pdf';
                  link.target = '_blank';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                Download
              </Button>
            </Box>
          )}

          {!error && open && pdfUrl && (
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={null}
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                loading={
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <CircularProgress size={24} />
                  </Box>
                }
              />
            </Document>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default PDFViewerDrawer;
